import { useMutation } from '@apollo/client';
import {
  setLoginMethod,
  useMtFreeApplicationsModal,
  useSocialLoginModal,
} from '@application/components';
import {
  CACHE_KEY_CONTACT_MESSAGE,
  FREE_APPLICATION_COUNTRIES,
  SUPPORTED_COUNTRIES,
} from '@application/constants';
import type { CachedContactMessage, Country, Language } from '@application/types';
import { SocialLoginMethod } from '@application/types';
import { useChangeLocale, useRouter } from '@application/utils';
import { Analytics } from '@application/utils/analytics';
import { graphql } from '@gql/gql';
import { CountryCode } from '@gql/graphql';
import type { AccountCreatedFromEnum, AccountCreateInput } from '@gql/graphql';
import type { JoinBuyerFormValues } from '@presentation/components';
import type { JoinBreederPageFormValues } from '@presentation/pages';
import { useCallback } from 'react';
import { useCurrentAccount } from '..';
import { useSendMessage } from './useCurrentAccount/useSendMessage';

export const ACCOUNT_CREATE = graphql(`
  mutation AccountCreate($data: AccountCreateInput!) {
    createAccount(data: $data) {
      token
      account {
        ...CurrentAccountInfo
      }
    }
  }
`);

export type CreateAccountParams = {
  breederFormValues?: JoinBreederPageFormValues;
  buyerFormValues?: JoinBuyerFormValues;
  googleToken?: string;
  facebookToken?: string;
  // TOOD: Better api
  email?: string;
  firstName?: string;
  lastName?: string;
  imgUrl?: string;
  phone?: string;
  country?: CountryCode;
  createdFrom?: AccountCreatedFromEnum;
  hasADog?: boolean;
  marketingEmailConsent?: boolean;
};

export const useCreateAccount = () => {
  const { navigateToRoute, language, countryCode, infoFromCurrentPath } = useRouter();

  const { sendMessage, sendMessageError, sendMessageLoading } = useSendMessage();
  const { currentAccountUpdateCache } = useCurrentAccount();
  const { changeCountryAndLanguage } = useChangeLocale();
  const { show: showMtFreeApplicationsModal } = useMtFreeApplicationsModal();

  const {
    modalHook: { show: showUseSocialLoginModal },
  } = useSocialLoginModal();

  const [create, { loading: createLoading, error: createError }] = useMutation(ACCOUNT_CREATE, {
    onError: (err) => {
      if (['login/use-google', 'login/use-facebook'].includes(err.message)) {
        setLoginMethod(
          err.message === 'login/use-google'
            ? SocialLoginMethod.GOOGLE
            : SocialLoginMethod.FACEBOOK,
        );
        showUseSocialLoginModal();
      }

      throw err;
    },
  });

  const createAccount = useCallback(
    async (param: CreateAccountParams) => {
      const {
        googleToken,
        facebookToken,
        buyerFormValues,
        createdFrom,
        hasADog,
        breederFormValues,
        marketingEmailConsent,
      } = param;

      const email = breederFormValues?.email ?? buyerFormValues?.email ?? param.email;

      if (!email) {
        throw new Error('Email is required');
      }

      let createData: AccountCreateInput = {
        language,
        email,
        firstName: param.firstName,
        lastName: param.lastName,
        phone: param.phone,
        createdFrom,
        marketingEmailConsent,
        ...(hasADog !== undefined && { hasADog }),
      };

      if (param.imgUrl) {
        createData = {
          ...createData,
          imgs: {
            create: [
              {
                small: param.imgUrl,
                medium: param.imgUrl,
                large: param.imgUrl,
              },
            ],
          },
        };
      }

      let country =
        breederFormValues?.country ?? buyerFormValues?.country ?? param.country ?? CountryCode.BE;

      const otherCountry = country;

      if (!SUPPORTED_COUNTRIES.includes(country)) {
        country = CountryCode.EU;
      }
      if (country) {
        createData = {
          ...createData,
          otherCountry,
          address: {
            create: {
              country,
            },
          },
        };
      }
      let method = 'Email';
      if (googleToken) {
        createData.googleToken = googleToken;
        method = 'Google';
      } else if (facebookToken) {
        createData.facebookToken = facebookToken;
        method = 'Facebook';
      } else if (buyerFormValues) {
        const { password, firstName, lastName } = buyerFormValues;
        createData = { ...createData, email, password, firstName, lastName };
      } else if (breederFormValues) {
        const { password, firstName, lastName } = breederFormValues;
        createData = {
          ...createData,
          email,
          password,
          firstName,
          lastName,
          otherCountry,
          dogBreeder: {
            create: {
              displayEmail: email,
              displayName: `${firstName} ${lastName}`,
              displayPhone: param.phone,
            },
          },
        };
      }

      const isBreeder = !!breederFormValues;
      const isInternationalVersion = countryCode === CountryCode.EU;
      let userLanguage: Language = language;
      if (isInternationalVersion) {
        switch (country) {
          case CountryCode.CH:
          case CountryCode.DE:
            userLanguage = 'de';
            break;
          case CountryCode.NL:
            userLanguage = 'nl';
            break;
          default:
            userLanguage = 'fr';
        }
      }
      // Create the account
      const account = await create({
        variables: { data: { ...createData, language: userLanguage } },
      });

      Analytics.sendSignupEvent(method, isBreeder ? 'breeder' : 'customer');

      currentAccountUpdateCache(
        account?.data?.createAccount?.account,
        account?.data?.createAccount?.token,
      );

      if (isBreeder) {
        changeCountryAndLanguage(userLanguage, country.toLowerCase() as Country);
        return navigateToRoute(
          'mtOnboarding',
          isInternationalVersion
            ? {
                newCountry: country?.toLowerCase() as Country,
                newLanguage: userLanguage,
              }
            : {},
        );
      }

      // Send cached message from contact if exists, else redirect
      const cachedMessageString = localStorage.getItem(CACHE_KEY_CONTACT_MESSAGE);
      if (cachedMessageString) {
        const message: CachedContactMessage = JSON.parse(cachedMessageString);
        localStorage.removeItem(CACHE_KEY_CONTACT_MESSAGE);
        await sendMessage(message.breederId, message.message);
        return navigateToRoute('mtMessages');
      }

      if (infoFromCurrentPath?.state?.returnInfo) {
        const { params, route, search } = infoFromCurrentPath.state
          .returnInfo as typeof infoFromCurrentPath;
        changeCountryAndLanguage(userLanguage, country.toLowerCase() as Country);

        navigateToRoute(route, {
          params,
          search,
          ...(isBreeder && isInternationalVersion
            ? {
                newCountry: country?.toLowerCase() as Country,
                userLanguageuage: userLanguage,
              }
            : {}),
        });
      }

      // Show free application for buyers in FR NL DE
      if (
        !isBreeder &&
        infoFromCurrentPath.route === 'joinBuyer' &&
        buyerFormValues?.country &&
        FREE_APPLICATION_COUNTRIES.includes(buyerFormValues.country)
      ) {
        showMtFreeApplicationsModal();
      }
    },
    [
      create,
      currentAccountUpdateCache,
      infoFromCurrentPath.state?.returnInfo,
      infoFromCurrentPath.route,
      countryCode,
      language,
      navigateToRoute,
      sendMessage,
      changeCountryAndLanguage,
      showMtFreeApplicationsModal,
    ],
  );

  return {
    createAccountLoading: createLoading || sendMessageLoading,
    createAccountError: createError || sendMessageError,
    createAccount,
  };
};
